import { Highlight, VStack } from "@chakra-ui/react";
import Link from "next/link";
import { useTextVariant } from "@/theme/hooks/useSectionStyle";
import { Text } from "@/elements";
import { scrollToElement } from "@/utils/scrollToElement";
import ModellingImg from "public/vdi/3d-modeling.png";
import RenderingSimulationimg from "public/vdi/rendering-simulation.png";
import ProductDesignImg from "public/vdi/product-design.png";
import VideoeditingImg from "public/vdi/video-editing.png";

type DescProps = {
  subTitle: string;
  text: string;
  highlight: string[];
};

const DescDisplay = ({ description }: { description: DescProps }) => {
  const TextVariant = useTextVariant();
  const { text, highlight } = description;
  return (
    <VStack gap={6} align={"left"} h="full" py={4} w="full">
      <Text variant={TextVariant} color="text.100" fontWeight={600} marginBottom={4}>
        {description.subTitle}
      </Text>
      <Text variant={TextVariant} color="text.100">
        <Highlight styles={{ fontWeight: 600, color: "text.100" }} query={highlight}>
          {text}
        </Highlight>
      </Text>
      <Text variant={TextVariant} color="text.100" fontStyle={"italic"}>
        Note : Need support for something specific?
        <br />
        <Link
          href="#contact-us"
          onClick={(e) => {
            e.preventDefault();
            scrollToElement("contact-us", 120);
          }}
          style={{ textDecoration: "underline", color: "#1E87F0", fontWeight: 700 }}
        >
          Reach out
        </Link>{" "}
        — our platform is optimized for a wide range of industry workflows.
      </Text>
    </VStack>
  );
};

export const APPS_DATA = [
  {
    title: "3D Modelling & Animation ",
    key: "3d-modelling",
    children: (
      <DescDisplay
        description={{
          subTitle: "Create. Animate. Iterate — Without Hardware Bottlenecks.",
          text: "OneMind’s infrastructure is optimized for professional 3D content creation tools like Autodesk Alias, Pixologic ZBrush, Blender, and Maxon Cinema 4D. Creative teams can sculpt, animate, and visualize in real time with multi-threaded CPU performance and GPU-accelerated rendering — delivering fluid workflows even during complex scene development. ",
          highlight: [
            "Autodesk Alias",
            "Pixologic ZBrush",
            "Blender",
            "Maxon Cinema 4D.",
          ],
        }}
      />
    ),
    image: ModellingImg,
    imageHeight: "350px",
  },
  {
    title: "Rendering & Simulation  ",
    key: "rendering-simulation",
    children: (
      <DescDisplay
        description={{
          subTitle: "High-Fidelity Visuals with Real-Time Feedback.",
          text: "Render large-scale scenes and visual effects using GPU-accelerated tools such as Chaos Group V-Ray, Autodesk Arnold, SideFX Houdini Karma, Corona Render, and Pixar Renderman. OneMind supports both real-time and offline rendering pipelines, empowering artists and engineers to iterate quickly, reduce render times, and simulate photorealistic outputs with confidence. ",
          highlight: [
            "Chaos Group V-Ray",
            "Autodesk Arnold",
            "SideFX Houdini Karma",
            "Corona Render",
            "Pixar Renderman.",
          ],
        }}
      />
    ),
    image: RenderingSimulationimg,
    imageHeight: "350px",
  },
  {
    title: "Post-Production & Video Editing",
    key: "video-editing",
    children: (
      <DescDisplay
        description={{
          subTitle: "Streamline Editing, Effects, and Color Workflows.",
          text: "From video editing to visual effects and audio post, OneMind handles demanding media applications like Adobe Premiere Pro, Blackmagic DaVinci Resolve, Foundry Nuke, Avid Media Composer, and Adobe Audition. With support for high-resolution media timelines, fast encoding, and GPU-driven playback, remote creators and studios can work without compromise. ",
          highlight: [
            "Adobe Premiere Pro",
            "Blackmagic DaVinci Resolve",
            "Foundry Nuke",
            "Avid Media Composer",
            "Adobe Audition",
          ],
        }}
      />
    ),
    image: VideoeditingImg,
    imageHeight: "350px",
  },
  {
    title: "Product Design & Engineering",
    key: "product-design",
    children: (
      <DescDisplay
        description={{
          subTitle: "Precision Meets Power — Perfect for CAD & Industrial Applications.",
          text: "Engineers and designers rely on tools like Dassault Systèmes SolidWorks, Autodesk AutoCAD, Autodesk Inventor, Robert McNeel Rhino, and Siemens Solid Edge — all fully supported on OneMind's virtual desktop platform. Backed by GPU acceleration, responsive I/O, and concurrency-based licensing, our solution enables seamless modeling, simulation, and visualization across your entire design pipeline.",
          highlight: [
            "Dassault Systèmes",
            " SolidWorks",
            "Autodesk AutoCAD",
            "Autodesk Inventor",
            "Robert McNeel Rhino",
            "Siemens Solid Edge",
          ],
        }}
      />
    ),
    image: ProductDesignImg,
    imageHeight: "350px",
  },
];

export const APPS_LIST = [
  {
    label: "3D Modelling & Animation",
    key: "3d-modelling",
  },
  {
    label: "Rendering & Simulation",
    key: "rendering-simulation",
  },
  {
    label: "Post-Production & Video Editing",
    key: "video-editing",
  },
  {
    label: "Product Design & Engineering",
    key: "product-design",
  },
];

export const APPS_CONSTANTS = {
  title: "Applications We Support ",
  desc: "From design and rendering to post-production and spatial analysis, OneMind’s virtual infrastructure is engineered for the tools professionals rely on daily. Whether you're building 3D models, editing high-resolution video, or simulating engineering designs, our VDI and GPU-backed environments are tuned for performance, responsiveness, and compatibility. ",
};
